import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import moment from 'moment-timezone';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import memoize from 'lodash/memoize';

import {capitalize, truncateMiddle} from 'common/util/string';
import {
  LiftCell,
  LiftRow,
  NON_INTERVALIZED_VALUE,
  RetentionApiChartType,
  RetentionBookmarkParams,
  RetentionDataTableCell,
  RetentionDataTableRow,
  TableCell,
} from 'common/report/retention/util/types';
import {FormattedRow, RawCell, RetentionType} from 'common/report/retention/util/types';
import {
  Addiction,
  isUnsegmentedData,
  KeyedByDate,
  KeyedBySegment,
  Retention,
  RetentionData,
  RetentionQueryResponse,
  RetentionResponseMetadata,
  SegmentKeyToPath,
  SuccessResponse,
} from 'common/report/retention/models/types';
import {AGGREGATED_ROW_NAME} from 'common/report/retention/util/constants';
import {RetentionQuery} from 'common/report/retention/models/retention-query';
import colors, {getChartSegmentColorCombo} from 'common/util/colors';
import {formatNumber, formatPercent, getRetentionBucketName} from 'common/report/retention/util';
import {getSegmentPropertyType, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {
  CellRendererFunc,
  ColumnGroup,
  CommonDataTableFields,
  TableColumn,
  TableColumnType,
  TableDataType,
} from 'common/components/data-table/types';
import {ChartType, PropertyType, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {LiftType} from 'common/report/util/lift/types';
import {DEFAULT_LIFT_COLUMN_WIDTH} from 'common/components/data-table/constants';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {TrendDataRow} from 'common/components/data-table/trend-data/types';
import {formatLiftPercent} from 'common/report/util/lift';
import {flattenQueryResponse} from 'common/report/retention/util/request';
import {SectionItem} from 'common/components/items-menu/types';
import {TIMEZONES} from 'common/constants';

import {getDataGroupName, getGroupByName, getSegmentName, isSegmented} from './format';

const MAX_TITLE_CELL_LENGTH = 60;

export type BaseRow = {
  date: Date;
  cohort_size: number;
  data: number[];
  rates?: number[];
  complete_date: Array<Date | null>;
};

export type SegmentRow = BaseRow & {
  segment: string;
};

export type Row = BaseRow | SegmentRow;
/** Aggregate row: data + optional segment, but no date */
export type AverageRow = Omit<BaseRow, 'date'> & {
  segment?: string;
  /** not calculated the same way as normal rows */
  percent_data?: number[];
};

export type SuperRow = Row | AverageRow;

/** A collection of dated rows and their aggregate, belonging to a segment. */
export type RowWithAggregate = {
  /** An aggregate of all of the rows in datedRows. */
  aggregate: AverageRow;
  /** Collection of rows for this segment, ordered by birth interval. */
  datedRows: Row[];
  /** If empty, represents unsegmented data. */
  segment?: string;
};

export function isSegmentRow(row: SuperRow): row is SegmentRow {
  return `segment` in row && `date` in row;
}

export function isAverageRow(row: SuperRow): row is AverageRow {
  return !(`date` in row);
}

export function isIntervalRow(row: SuperRow): row is BaseRow {
  return !(`segment` in row) && `date` in row;
}

const WITH_YEAR = `MMM D, YYYY`;
const WITHOUT_YEAR = `MMM D`;
export const formatDate = memoize(function (date: Date, timezone: string, includeYear = true): string {
  return moment.tz(date, timezone).format(includeYear ? WITH_YEAR : WITHOUT_YEAR);
});

function getRowHead(row: SuperRow, retentionType: RetentionType, timezone: string, isGroupedRow) {
  if (`segment` in row && !isGroupedRow) {
    // prefer segment even for average rows
    return stringifySegment(row.segment);
  } else if (`date` in row) {
    return formatDate(row.date, timezone);
  } else {
    // only use this when no segment is available
    return AGGREGATED_ROW_NAME(retentionType);
  }
}

export function getCellsForRow(retentionType: RetentionType, timezone: string) {
  return function getCellsForRow(row: SuperRow, idx: number, isGroupedRow = false): FormattedRow<RawCell> {
    const data = row.data.map(getCellForIndex(row, timezone));
    const rowHead = getRowHead(row, retentionType, timezone, isGroupedRow);
    const truncatedHead =
      rowHead.length > MAX_TITLE_CELL_LENGTH ? truncateMiddle(rowHead, MAX_TITLE_CELL_LENGTH) : rowHead;

    return {
      cohort_size: row.cohort_size,
      data,
      display_cohort_size: formatNumber(row.cohort_size),
      head: rowHead,
      display_head: truncatedHead,
      raw_head: rowHead,
      selection_color: getChartSegmentColorCombo(idx).primary,
    };
  };
}

function stringifySegment(seg: string | string[]): string {
  if (Array.isArray(seg)) {
    return seg.join(` / `);
  } else {
    return seg;
  }
}

export function getCellForIndex(row: SuperRow, timezone: string) {
  return function getCellForIndex(value: number, valIdx: number): RawCell {
    const completeDate = row.complete_date[valIdx];
    const incomplete = !!completeDate;
    let enteredValue = null;

    if (`segment` in row) {
      enteredValue = stringifySegment(row.segment);
    } else if (isAverageRow(row)) {
      enteredValue = `$average`; // this doesn't get consumed anywhere
    } else {
      enteredValue = formatDate(row.date, timezone);
    }

    let cohortSize: number, percent: number;
    // If `rates` is defined in row, it implies that row is a retention data.
    // If `rates` is undefined in row, it implies that row is a frequency/addiction data.
    // cohortSize is defined differently depending on the type of data
    if (`rates` in row) {
      percent = row.rates[valIdx];
      cohortSize = percent > 0 ? Math.round((value / percent) * 10000) / 10000 : 0;
    } else {
      cohortSize = row.cohort_size;
      percent = `percent_data` in row ? row.percent_data[valIdx] : cohortSize > 0 ? value / cohortSize : 0;
    }

    return {
      date: `date` in row ? row.date : null,
      cohort_size: cohortSize,
      complete_date: incomplete && formatDate(completeDate, timezone, false),
      incomplete,
      percent,
      value,
      formatted_percent: formatPercent(percent),
      percent_drop_off_value: formatPercent(1 - percent),
      formatted_value: formatNumber(value),
      absolute_drop_off_value: formatNumber(cohortSize - value),
      selectable: true,
      entered_value: enteredValue,
    };
  };
}

export const DEFAULT_UNSEG_BUCKET = `Overall`;

export function getUiRowsData(
  queryResponse: RetentionQueryResponse,
  bookmarkParams: RetentionBookmarkParams,
  timezone,
): {
  formattedUiRows: FormattedRow[];
  formattedComparisonRowsData: Map<string, FormattedRow>;
  formattedLiftRowsData: Map<string, LiftRow>;
} {
  const {metadata, data, compareToPastData, comparisonMetadata, liftData} = flattenQueryResponse(
    queryResponse,
    bookmarkParams.group_by,
  );

  return getUiRowsDataFromParsed(
    data,
    metadata,
    bookmarkParams,
    timezone,
    compareToPastData,
    comparisonMetadata,
    liftData,
  );
}

export function getUiRowsDataFromParsed(
  data: RetentionData,
  metadata: RetentionResponseMetadata,
  bookmarkParams: RetentionBookmarkParams,
  timezone: any,
  compareToPastData: RetentionData,
  comparisonMetadata: {incomplete_buckets?: {[date: string]: string[]}},
  liftData: RetentionData,
) {
  let formattedUiRows = Array<FormattedRow<RawCell>>();
  let formattedComparisonRowsData = new Map<string, FormattedRow>();
  let formattedLiftRowsData = new Map<string, LiftRow>();
  let liftRowsDataMap = new Map<string, RowWithAggregate>();

  const dataRows = getFormattedRowData(data, metadata, bookmarkParams.group_by, timezone);

  const compareToPastDataRows = compareToPastData
    ? getFormattedRowData(compareToPastData, comparisonMetadata, bookmarkParams.group_by, timezone)
    : [];

  const liftDataRows = liftData ? getFormattedRowData(liftData, metadata, bookmarkParams.group_by, timezone) : [];

  function mapFormattedRowsData(formattedUIRows): Map<string, FormattedRow> {
    return new Map(
      formattedUIRows.map((segmentRow) => [
        segmentRow.raw_head,
        {...segmentRow, selection_color: getCompareRowColor(segmentRow)},
      ]),
    );
  }

  if (hasRetentionData(dataRows) || hasRetentionData(compareToPastDataRows)) {
    formattedUiRows = mapToUiRows(dataRows, timezone, bookmarkParams);

    const formattedComparedUiRows =
      compareToPastDataRows.length > 0 ? mapToUiRows(compareToPastDataRows, timezone, bookmarkParams) : [];

    formattedComparisonRowsData = mapFormattedRowsData(formattedComparedUiRows);
  }
  if (hasRetentionData(liftDataRows)) {
    const formattedLiftUiRows = mapLiftToUiRows(liftDataRows, timezone, bookmarkParams);

    formattedLiftRowsData = mapFormattedRowsData(formattedLiftUiRows);

    // build another construct of lift data, that only has what we need
    liftRowsDataMap = new Map(liftDataRows.map((liftRow) => [liftRow.aggregate.segment, liftRow]));
  }

  function getCompareRowColor(compareRow) {
    return formattedUiRows.find((row) => row.raw_head === compareRow.raw_head).selection_color;
  }
  return {formattedUiRows, formattedComparisonRowsData, formattedLiftRowsData, liftRowsDataMap};
}

export function mapLiftToUiRows(
  liftRowData: RowWithAggregate[],
  timezone: string,
  bookmarkParams: RetentionBookmarkParams,
): LiftRow[] {
  let rows = [];
  if (
    bookmarkParams.chart_type === RetentionApiChartType.Table ||
    bookmarkParams.chart_type === RetentionApiChartType.Line
  ) {
    rows = liftRowData.map((group) => {
      const averageRow = group.aggregate;
      const liftRow = getLiftRow(bookmarkParams.retention_type, timezone, averageRow);

      if (isSegmented(bookmarkParams.group_by)) {
        liftRow.raw_head = liftRow.display_head = liftRow.head = stringifySegment(group.aggregate.segment);
      }
      if (bookmarkParams.date_unit !== NON_INTERVALIZED_VALUE) {
        liftRow.groupedRows = group.datedRows.map((groupedRow) =>
          getLiftRow(bookmarkParams.retention_type, timezone, groupedRow, true),
        );
      }

      return liftRow;
    });
  } else if (
    bookmarkParams.chart_type === RetentionApiChartType.RetentionTrend ||
    bookmarkParams.chart_type === RetentionApiChartType.RetentionTrendMetric
  ) {
    const buildDataFromDatedRows = function (datedRows) {
      return datedRows.reduce((accumData, dr) => {
        if (dr.data.length > bookmarkParams.retention_bucket) {
          accumData.push(
            getLiftCellForIndex(dr, timezone)(
              dr.data[bookmarkParams.retention_bucket],
              bookmarkParams.retention_bucket,
            ),
          );
        }
        return accumData;
      }, []);
    };
    if (isSegmented(bookmarkParams.group_by)) {
      // segmented
      rows = liftRowData.map((segmentRow) => {
        const data = buildDataFromDatedRows(segmentRow.datedRows);
        const segment = stringifySegment(segmentRow.aggregate.segment);
        return {
          head: segment,
          raw_head: segment,
          display_head: segment,
          data,
        };
      });
    } else {
      // unsegmented
      const [row] = liftRowData;
      rows = [
        {
          display_head: DEFAULT_UNSEG_BUCKET,
          head: DEFAULT_UNSEG_BUCKET,
          raw_head: DEFAULT_UNSEG_BUCKET,
          data: buildDataFromDatedRows(row.datedRows),
        },
      ];
    }
  } else {
    rows = [];
  }
  return rows;
}

function getLiftRow(retentionType: RetentionType, timezone: string, row: SuperRow, isGroupedRow = false): LiftRow {
  const data = row.data.map(getLiftCellForIndex(row, timezone));
  const rowHead = getRowHead(row, retentionType, timezone, isGroupedRow);
  const truncatedHead =
    rowHead.length > MAX_TITLE_CELL_LENGTH ? truncateMiddle(rowHead, MAX_TITLE_CELL_LENGTH) : rowHead;
  return {
    data,
    head: rowHead,
    raw_head: rowHead,
    display_head: truncatedHead,
  };
}

function getLiftCellForIndex(row: SuperRow, timezone: string) {
  return function (value: number, valIdx: number): LiftCell {
    const completeDate = row.complete_date[valIdx];
    const incomplete = !!completeDate;
    let enteredValue = null;

    if (`segment` in row) {
      enteredValue = stringifySegment(row.segment);
    } else if (isAverageRow(row)) {
      enteredValue = `$average`; // this doesn't get consumed anywhere
    } else {
      enteredValue = formatDate(row.date, timezone);
    }

    const percent = row.rates ? row.rates[valIdx] : 0;

    return {
      date: `date` in row ? row.date : null,
      complete_date: incomplete && formatDate(completeDate, timezone, false),
      entered_value: enteredValue,
      value,
      formatted_percent: formatLiftPercent(percent),
      percent,
      incomplete,
    };
  };
}

/**
 * Turn a collection of RowWithAggregates into a flat list of rows.
 *
 * For unsegmented retention and frequency, explode the single RowWithAggregate into
 * a row for the aggregate and rows for all the birth intervals.
 *
 * For each segment of segmented retention and frequency, choose the aggregate row.
 */
export function mapToUiRows(
  rowData: RowWithAggregate[],
  timezone: string,
  bookmarkParams: RetentionBookmarkParams,
): Array<FormattedRow<RawCell>> {
  let rows;

  const rowToCell = getCellsForRow(bookmarkParams.retention_type, timezone);

  if (
    bookmarkParams.chart_type === RetentionApiChartType.Table ||
    bookmarkParams.chart_type === RetentionApiChartType.Line
  ) {
    rows = rowData.map((group, idx) => {
      const averageRow = group.aggregate;
      const fmtRow = rowToCell(averageRow, idx);

      if (isSegmented(bookmarkParams.group_by)) {
        fmtRow.raw_head = fmtRow.display_head = fmtRow.head = stringifySegment(group.aggregate.segment);
      }

      if (bookmarkParams.date_unit !== NON_INTERVALIZED_VALUE) {
        fmtRow.display_cohort_size = `100%`;
        fmtRow.groupedRows = group.datedRows.map((groupedRow, idx) => rowToCell(groupedRow, idx, true));
      }

      return fmtRow;
    });
  } else if (
    bookmarkParams.chart_type === RetentionApiChartType.RetentionTrend ||
    bookmarkParams.chart_type === RetentionApiChartType.RetentionTrendMetric
  ) {
    if (isSegmented(bookmarkParams.group_by)) {
      rows = rowData.map((segmentRow, idx) => {
        const data = segmentRow.datedRows.reduce((data, dr) => {
          if (dr.data.length > bookmarkParams.retention_bucket) {
            data.push(
              getCellForIndex(dr, timezone)(dr.data[bookmarkParams.retention_bucket], bookmarkParams.retention_bucket),
            );
          }
          return data;
        }, []);
        const segment = stringifySegment(segmentRow.aggregate.segment);
        return {
          cohort_size: segmentRow.aggregate.cohort_size,
          display_cohort_size: formatNumber(segmentRow.aggregate.cohort_size),
          data,
          display_head: segment,
          head: segment,
          raw_head: segment,
          selection_color: getChartSegmentColorCombo(idx).primary,
        };
      });
    } else {
      const [row] = rowData;
      rows = [
        {
          cohort_size: row.aggregate.cohort_size,
          display_cohort_size: formatNumber(row.aggregate.cohort_size),
          display_head: DEFAULT_UNSEG_BUCKET,
          head: DEFAULT_UNSEG_BUCKET,
          raw_head: DEFAULT_UNSEG_BUCKET,
          selection_color: getChartSegmentColorCombo(0).primary,
          data: row.datedRows.reduce((data, datedRow) => {
            if (datedRow.data.length > bookmarkParams.retention_bucket) {
              data.push(
                getCellForIndex(datedRow, timezone)(
                  datedRow.data[bookmarkParams.retention_bucket],
                  bookmarkParams.retention_bucket,
                ),
              );
            }
            return data;
          }, []),
        },
      ];
    }
  } else {
    rows = [];
  }

  return rows;
}

export function mapToTrendDataRows({
  bookmark,
  formattedUiRows,
  numSegments,
  segmentKeyToPath,
  formattedComparisonRowsData,
  formattedLiftRowsData,
}: {
  bookmark: RetentionBookmarkParams;
  formattedUiRows: Array<FormattedRow<RawCell>>;
  numSegments: number;
  segmentKeyToPath: SegmentKeyToPath;
  formattedComparisonRowsData: Map<string, FormattedRow<RawCell>>;
  formattedLiftRowsData: Map<string, LiftRow>;
}): TrendDataRow[] {
  const selectedSegmentSet = new Set(bookmark.selected_segments);
  return formattedUiRows.map((row) => {
    const segmentValues = numSegments > 1 ? segmentKeyToPath[row.display_head] : [row.display_head];

    const rowDataLen = row.data.length;
    const comparisonRow = formattedComparisonRowsData.get(row.raw_head);
    const liftRow = formattedLiftRowsData.get(row.raw_head);

    function valuePlusTimestamp(cells: RawCell[] | LiftCell[]) {
      return cells.slice(0, rowDataLen).map((d) => {
        return {
          value: bookmark.percentage_view ? d.percent : d.value,
          timestamp: d.date.valueOf(),
        };
      });
    }

    return {
      rowId: row.raw_head,
      selectionColor: row.selection_color,
      isSelected: selectedSegmentSet.has(row.raw_head),
      segmentValues,
      cohortSize: row.cohort_size,
      data: valuePlusTimestamp(row.data),
      ...(comparisonRow
        ? {
            //slice in order to ignore "extra" date buckets in comparison data
            timeComparisonData: valuePlusTimestamp(comparisonRow.data),
          }
        : {}),
      ...(liftRow
        ? {
            //slice in order to ignore "extra" date buckets in comparison data
            liftComparisonData: valuePlusTimestamp(liftRow.data),
          }
        : {}),
    };
  });
}

/**
 * Add color info to the table cell
 *
 * @param cell retention table cell
 * @param max percentage in the table
 * @param min percentage in the table
 * @return {TableCell} table cell data with color
 */
function calculateTableCellColor(cell: RawCell, max: number, min: number): TableCell {
  let combo = colors.getRetentionColorCombo(1, false, cell.incomplete);
  // prevent an edge case where there's only one cell available
  if (max > min) {
    // math to determine the calibrated step value
    const heatPercent = (cell.percent - min) / (max - min);
    combo = colors.getRetentionColorCombo(heatPercent, false, cell.incomplete);
  }
  return {
    ...cell,
    backgroundColor: combo.primary,
    textColor: combo.secondary,
  };
}

export function mapToRetentionCurveTableRows({
  formattedUiRows,
  bookmarkParams,
  segmentKeyToPath,
  formattedLiftRowsData,
}: {
  formattedUiRows: FormattedRow[];
  bookmarkParams: RetentionBookmarkParams;
  segmentKeyToPath: SegmentKeyToPath;
  formattedLiftRowsData: Map<string, LiftRow>;
}): RetentionDataTableRow[] {
  const selectedSegments = new Set<string>(bookmarkParams.selected_segments);
  const expandedSegments = new Set<string>(bookmarkParams.expanded_segments);

  const percentages = formattedUiRows
    .flatMap((row) => [row, ...(row.groupedRows ?? [])])
    .map((row) => row.data.filter((cell) => !cell.incomplete).map((cell) => cell.percent))
    .flat();

  const maxPercent = percentages.reduce((a, b) => Math.max(a, b), -Infinity);
  const minPercent = percentages.reduce((a, b) => Math.min(a, b), Infinity);

  const hasLift = !!formattedLiftRowsData.size;
  const isPercentageView = bookmarkParams.percentage_view;

  const formatRow = (row: FormattedRow<RawCell>): FormattedRow<TableCell> => {
    const formattedRow = {
      ...row,
      data: row.data.map(
        (cell: RawCell): TableCell => ({
          ...(hasLift ? cell : calculateTableCellColor(cell, maxPercent, minPercent)),
          clickable: true,
          //TODO: add tooltip info
          ...(cell.complete_date
            ? {
                tooltip: `${capitalize(
                  getDataGroupName(bookmarkParams.group_by, true),
                )} will continue to qualify until the end of ${cell.complete_date}`,
              }
            : {}),
        }),
      ),
    } as FormattedRow<TableCell>;

    if (row.groupedRows) {
      formattedRow.groupedRows = row.groupedRows.map(formatRow);
    }

    return formattedRow;
  };
  const rows: Array<FormattedRow<TableCell>> = formattedUiRows.map(formatRow);

  return rows.map((row) => {
    const selectedSegment = selectedSegments.has(row.raw_head);
    let segmentFields = {};
    if (isSegmented(bookmarkParams.group_by)) {
      segmentFields = segmentKeyToPath[row.head as string].reduce((accum, segmentValue, i) => {
        accum[`${CommonDataTableFields.SegmentPrefix}${i}`] = segmentValue;
        return accum;
      }, {});
    } else {
      segmentFields[`${CommonDataTableFields.SegmentPrefix}0`] = row.head;
    }

    let baselineRow: FormattedRow<RawCell> = null;
    if (hasLift) {
      const baselineRowKey = getLiftBaselineSegmentRawHead(row, segmentKeyToPath, bookmarkParams.lift_comparison);
      baselineRow = formattedUiRows.find((row) => row.raw_head === baselineRowKey);
    }

    const liftRow = formattedLiftRowsData.get(row.raw_head);

    const dataTableRow = {
      identifier: row.raw_head,
      cohortSize: row.cohort_size,
      displayCohortSize: row.display_cohort_size,
      displayHead: row.display_head,
      head: row.head,
      ...segmentFields,
      date: row.date,
      rawHead: row.raw_head,
      cells: row.data as RetentionDataTableCell[],
      isSelected: !!selectedSegment,
      ...extractLiftFields({liftRow, baselineRow, isPercentageView}),
    } as RetentionDataTableRow;

    if (selectedSegment) {
      dataTableRow.checkedColor = row.selection_color;
    }

    if (row.groupedRows?.length) {
      dataTableRow.isCollapsed = !expandedSegments.has(row.raw_head);
      dataTableRow.groupedRows = row.groupedRows.map((groupedRow, index) => {
        let groupedLiftRow, groupedBaselineRow;
        if (liftRow && liftRow.groupedRows && baselineRow) {
          groupedLiftRow = liftRow.groupedRows[index];
          groupedBaselineRow = baselineRow.groupedRows[index];
        }
        return {
          identifier: groupedRow.raw_head,
          cohortSize: groupedRow.cohort_size,
          displayCohortSize: groupedRow.display_cohort_size,
          displayHead: groupedRow.display_head,
          head: groupedRow.head,
          date: groupedRow.date,
          rawHead: groupedRow.raw_head,
          cells: groupedRow.data as RetentionDataTableCell[],
          ...extractLiftFields({liftRow: groupedLiftRow, baselineRow: groupedBaselineRow, isPercentageView}),
          isGroupedRow: true,
        };
      });
    }

    return dataTableRow;
  });
}

function extractLiftFields({
  liftRow,
  baselineRow,
  isPercentageView,
}: {
  liftRow: {data: LiftCell[]} | null;
  baselineRow: FormattedRow<RawCell> | null;
  isPercentageView: boolean;
}) {
  let liftComparisons = [];
  if (liftRow && baselineRow) {
    liftComparisons = liftRow.data.map((liftCell, index) => [
      // goes in the pill
      `${CommonDataTableFields.LiftComparisonDatePointPrefix}${index}`,
      isPercentageView ? liftCell.percent : liftCell.value,
    ]);
    liftComparisons = liftComparisons.concat(
      baselineRow.data.map((cell, index) => [
        // goes in the tooltip
        `${CommonDataTableFields.LiftComparisonExtraFieldPrefix}${index}`,
        (isPercentageView ? cell.percent : cell.value) || 0,
      ]),
    );
  }

  return Object.fromEntries([...liftComparisons]);
}

export function getRetentionCurveTableColumnGroups({
  formattedUiRows,
  bookmarkParams,
  getDataCellRenderer,
}: {
  formattedUiRows: FormattedRow[];
  bookmarkParams: RetentionBookmarkParams;
  getDataCellRenderer: (hasLift, isPct) => CellRendererFunc;
}): ColumnGroup[] {
  const hasLift = bookmarkParams.lift_comparison?.type === LiftType.Segment;
  const isPct = bookmarkParams.percentage_view;
  const dataCellRenderer = getDataCellRenderer(hasLift, isPct);
  const numColumns =
    formattedUiRows && formattedUiRows.length ? Math.max(...formattedUiRows.map((row) => row.data.length)) : 0;

  const headerColumns = getHeaderColumns(bookmarkParams);
  const dataColumns = getDataColumns({numColumns, bookmarkParams, dataCellRenderer, isPct, hasLift});
  return [{columns: headerColumns}, {columns: dataColumns}];
}

function getHeaderColumns(bookmarkParams: RetentionBookmarkParams) {
  function getSegmentedName(groupBy: Segment[]) {
    if (isSegmented(groupBy)) {
      return getSegmentName(groupBy) || `Cohort`;
    } else {
      return `Date`;
    }
  }

  const headerColumns: TableColumn[] = [];

  const lastSegmentIndex = isSegmented(bookmarkParams.group_by) ? bookmarkParams.group_by.length - 1 : 0;
  const cellRenderer = ({item, context}) => {
    let result;
    if (item.isGroupedRow) {
      // subRows should show their own label (e.g. date string) instead of the segment values
      result = context.field === `${CommonDataTableFields.SegmentPrefix}${lastSegmentIndex}` ? item.displayHead : ``;
    } else {
      // regular rows show the segmentation values (e.g. $mp-segment-0 -> "Chrome", $mp-segment-1 -> "USA")
      result = item[context.field];
    }
    return result;
  };

  const commonColumnParams = {
    fixedWidth: true,
    type: TableColumnType.Segment as const,
    sticky: true,
    cellRenderer,
  };
  if (isSegmented(bookmarkParams.group_by)) {
    bookmarkParams.group_by.forEach((groupBy, i) => {
      const contentType = getSegmentPropertyType(groupBy);
      const segmentField = `${CommonDataTableFields.SegmentPrefix}${i}`;
      const column = {
        ...commonColumnParams,
        field: segmentField,
        title: getGroupByName(groupBy),
        isSortable: true,
        contentType,
      };
      headerColumns.push(column);
    });
  } else {
    const headerColumn = {
      ...commonColumnParams,
      field: `${CommonDataTableFields.SegmentPrefix}0`,
      title: getSegmentedName(bookmarkParams.group_by),
      isSortable: false,
      contentType: PropertyType.String,
    };
    headerColumns.push(headerColumn);
  }

  const title = `Total ${capitalize(getDataGroupName(bookmarkParams.group_by, true))}`;
  const cohortSize: TableColumn = {
    field: `size`,
    title,
    sortSelectTitle: title,
    type: TableColumnType.Data,
    contentType: TableDataType.Number,
    initialWidth: 120,
    fixedWidth: true,
    sticky: true,
    isSortable: isSegmented(bookmarkParams.group_by),
    sortTransform: (row) => {
      return row.cohortSize;
    },
    textColor: colors.secondaryTextColor,
    backgroundColor: colors.secondaryBackgroundColor,
    cellRenderer: ({item}) => item.displayCohortSize,
  };

  return [...headerColumns, cohortSize];
}

function getDataColumns({
  numColumns,
  bookmarkParams,
  dataCellRenderer,
  isPct,
  hasLift,
}: {
  numColumns: number;
  bookmarkParams: RetentionBookmarkParams;
  dataCellRenderer: CellRendererFunc<any>;
  isPct: boolean;
  hasLift: boolean;
}): TableColumn[] {
  const dataColumns = [];
  const dataColumnSortTransform = (row, field) => {
    return row.cells[field][isPct ? `percent` : `value`];
  };
  for (let i = 0; i < numColumns; i++) {
    const title = getRetentionBucketName({bucketIndex: i, bookmarkParams});
    const column: TableColumn = {
      field: `${i}`,
      title,
      sortSelectTitle: title,
      type: TableColumnType.Data,
      contentType: TableDataType.Number,
      initialWidth: 90,
      fixedWidth: true,
      last: i + 1 === numColumns,
      isSortable: isSegmented(bookmarkParams.group_by),
      sortTransform: dataColumnSortTransform,
      isSelectable: true,
      cellRenderer: dataCellRenderer,
      ...(hasLift
        ? {
            liftField: `${CommonDataTableFields.LiftComparisonDatePointPrefix}${i}`,
            liftExtra: `${CommonDataTableFields.LiftComparisonExtraFieldPrefix}${i}`,
            sortTransform: (row) => {
              return row[`${CommonDataTableFields.LiftComparisonDatePointPrefix}${i}`];
            },
            // set width of data table cells to be slightly wider to allow for the extra lift pill
            initialWidth: DEFAULT_LIFT_COLUMN_WIDTH,
          }
        : {}),
    };
    dataColumns.push(column);
  }
  return dataColumns;
}

function mapToCompleteDates(
  incompleteBuckets: Record<string, string[]>,
  date: string,
  tz: string,
  fallbackLength: number,
): Array<Date | null> {
  if (incompleteBuckets && incompleteBuckets[date]) {
    return incompleteBuckets[date].map((maybeDate) =>
      typeof maybeDate === `string` ? moment.tz(maybeDate, tz).toDate() : null,
    );
  } else {
    return new Array(fallbackLength).fill(null);
  }
}

type ChooseSelectedSegmentsResult = {
  selectedSegments: string[];
  /**
   * Flag indicating if any of the new selected segments overlaps with the prior selected segments.
   * If it's false (i.e. there's no overlapping segments), it means the query has changed
   * in a substantial way (e.g. add/removing a group by)
   */
  hasSavedSegments: boolean;
};

/**
 * Choose a new set of segments for the data.
 *
 * If some were previously selected, keep them. If none of the previous selections
 * exist in the data, select new ones.
 *
 * @param trimToSize if there are more saved segments than numSegments, slice them to the appropraiate length. Default: false
 * @param numSegments defaults to REPORT_LIMITS default number
 */
export function chooseSelectedSegments({
  data,
  savedSegments,
  groupBy,
  chartType,
  trimToSize = false,
  numSegments,
}: {
  data: FormattedRow[];
  savedSegments: string[];
  groupBy: Segment[];
  chartType: ChartType;
  trimToSize?: boolean;
  numSegments?: number;
}): ChooseSelectedSegmentsResult {
  numSegments =
    numSegments || (isSegmented(groupBy) ? REPORT_LIMITS[ReportName.Retention].segments[chartType].default : 1);
  const actualDataSegments = new Set(data.map((row) => row.raw_head));

  let selections: string[] = [];

  if (savedSegments.length) {
    const stillValidSegments = savedSegments.filter((saved) => actualDataSegments.has(saved));

    if (stillValidSegments.length > 0) {
      selections = trimToSize ? stillValidSegments.slice(0, numSegments) : stillValidSegments;

      return {selectedSegments: selections, hasSavedSegments: true};
    }
  }

  if (!isSegmented(groupBy)) {
    // for unsegmented, just choose the average row, which will be in position 0
    return {selectedSegments: [data[0].raw_head], hasSavedSegments: false};
  }

  return {selectedSegments: data.slice(0, numSegments).map((d) => d.raw_head), hasSavedSegments: false};
}

export function chooseExpandedSegments({data, query}: {data: FormattedRow[]; query: RetentionQuery}) {
  if (!query.isSegmented()) {
    // for unsegmented, expand the average row
    return [data[0].raw_head];
  } else {
    const expandedSegmentSet = new Set(query.getExpandedSegments());
    return data.map((d) => d.raw_head).filter((segmentName) => expandedSegmentSet.has(segmentName));
  }
}

/** For calculating row indices and offsets: is there an Average row showing? */
export function isAverageRowShowing(bookmarkParams: RetentionBookmarkParams) {
  return !isSegmented(bookmarkParams.group_by);
}

export function hasRetentionData(formattedRowData: RowWithAggregate[]) {
  return formattedRowData?.some((row) =>
    row.datedRows.some((datedRow) => datedRow.cohort_size !== 0 || datedRow.data.some((v) => v !== 0)),
  );
}

function sortByAscendingDate(a: Row, b: Row) {
  return a.date.valueOf() - b.date.valueOf();
}

/**
 * Transform the API response format into Rows where
 * - dates are aligned to start of day in project time
 * - segments are renamed
 * - rows are sorted by date or size
 * - retention and addiction data looks the same
 * - aggregate data is joined to the segment it represents
 **/
export function getFormattedRowData(
  data: RetentionData,
  metadata: SuccessResponse['metadata'],
  segmentGroups: Segment[],
  timezone: string,
): RowWithAggregate[] {
  if (!data || isEmpty(data)) {
    return [];
  }

  if (isUnsegmentedData(data, !isSegmented(segmentGroups))) {
    const rows = map(omit(data, `$average`), function (row, date) {
      const dateInProjectTime = moment.tz(date, timezone).toDate();
      const numbers = getRowData(row);
      return {
        ...numbers,
        date: dateInProjectTime,
        complete_date: mapToCompleteDates(metadata.incomplete_buckets, date, timezone, numbers.data?.length),
      };
    });

    rows.sort(sortByAscendingDate);

    let averageRow: AverageRow;
    if (`$average` in data) {
      const numbers = getRowData(data[`$average`]);
      averageRow = {
        ...numbers,
        complete_date: mapToCompleteDates(metadata.incomplete_buckets, `$average`, timezone, numbers.data?.length),
        percent_data: numbers.rates || computeAveragePercentages(numbers),
      };
    }

    return [
      {
        aggregate: averageRow,
        datedRows: rows,
      },
    ];
  }

  // segmented
  return constructMultiDateRowGroups(data, metadata, timezone);
}

/**
 * Collect segmented rows across birth intervals into a single RowWithAggregate per segment.
 */
function constructMultiDateRowGroups(
  data: KeyedByDate<KeyedBySegment<any>>,
  metadata: SuccessResponse['metadata'],
  projectTimezone: string,
): RowWithAggregate[] {
  const segmentMap = new Map<string, RowWithAggregate>();
  const incompleteBuckets = metadata.incomplete_buckets;

  for (const [bucket, , ...path] of traverseLeaves(pick(data, `$average`))) {
    const segment = path[0];
    const numbers = getRowData(bucket);

    segmentMap.set(segment, {
      aggregate: {
        ...numbers,
        segment,
        complete_date: mapToCompleteDates(incompleteBuckets, `$average`, projectTimezone, numbers.data.length),
        percent_data: numbers.rates || computeAveragePercentages(numbers),
      },
      datedRows: [],
      segment,
    });
  }
  for (const [bucket, date, ...path] of traverseLeaves(omit(data, `$average`))) {
    const segment = path[0];
    const numbers = getRowData(bucket);

    segmentMap.get(segment).datedRows.push({
      ...numbers,
      segment,
      date: moment.tz(date, projectTimezone).toDate(),
      complete_date: mapToCompleteDates(incompleteBuckets, date, projectTimezone, numbers.data.length),
    });
  }

  const groups = [...segmentMap.values()].sort(function (a, b) {
    return b.aggregate.cohort_size - a.aggregate.cohort_size;
  });

  for (const group of groups) {
    group.datedRows.sort(sortByAscendingDate);
  }

  return groups;
}

// Local helper functions

export function traverseLeaves(
  tree: KeyedByDate<KeyedBySegment<Addiction>>,
): Generator<[Addiction, string, string], void, void>;
export function traverseLeaves(
  tree: KeyedByDate<KeyedBySegment<Retention>>,
): Generator<[Retention, string, string], void, void>;

/** iteratively yield the leaves (retention or addiction data) of the response tree with their path. */
export function* traverseLeaves<TBucket>(tree: any): Generator<[TBucket, string, string], void, void> {
  function* recurse(tree: any, prefix: string[]) {
    if (isLeaf(tree)) {
      yield [tree, ...prefix];
    } else {
      for (const key of Object.keys(tree)) {
        yield* recurse(tree[key], [...prefix, key]);
      }
    }
  }

  yield* recurse(tree, []);
}

function isLeaf(value: any) {
  if (Array.isArray(value)) {
    return true;
  }

  return !!(typeof value === `object` && `counts` in value && `first` in value && Array.isArray(value.counts));
}

function getRowData(bucket: Addiction | Retention): Pick<BaseRow, 'cohort_size' | 'data' | 'rates'> {
  if (Array.isArray(bucket)) {
    return {
      cohort_size: bucket[0],
      data: bucket.slice(1),
    };
  } else {
    return {
      cohort_size: bucket.first,
      data: bucket.counts,
      rates: bucket.rates,
    };
  }
}

/** Construct an aggregate Row from all of the date interval Rows */
function computeAveragePercentages(numbers: Pick<BaseRow, 'cohort_size' | 'data'>): number[] {
  const percents = [];

  const {data, cohort_size: averageCohortSize} = numbers;

  for (let columnIndex = 0; columnIndex < data.length; columnIndex++) {
    const averageRetainedValue = data[columnIndex];
    const percent = averageCohortSize > 0 ? averageRetainedValue / averageCohortSize : 0;
    percents.push(percent);
  }
  return percents;
}

/*
 * What key should I use to get the Lift baseline segment for the row I have?
 * Returns e.g. "dashboards / United States"
 */
export function getLiftBaselineSegmentRawHead(
  row: FormattedRow,
  segmentKeyToPath: SegmentKeyToPath,
  liftComparison: LiftComparison,
) {
  const rowSegmentPath = segmentKeyToPath[row.raw_head];
  const liftBaselineSegmentPath = [...rowSegmentPath.slice(0, -1), liftComparison?.groupValue];
  return stringifySegment(liftBaselineSegmentPath);
}

export function generateRetentionBucketSectionItems({
  results,
  query,
  timezone,
}: {
  results: SuccessResponse;
  query: RetentionQuery;
  timezone: typeof TIMEZONES[number];
}): Array<SectionItem<number>> {
  const {retentionBucket: bucketIndex} = query;
  let items: Array<SectionItem<number>> = [];
  if (results) {
    const {metadata, data} = flattenQueryResponse(results, query.groupBy);
    const tableData = getFormattedRowData(data, metadata, query.groupBy, timezone);
    items = tableData[0]?.datedRows[0].data.map((_, i) => {
      return {
        label: getRetentionBucketName({
          bucketIndex: i,
          bookmarkParams: query.generateBookmark(),
        }),
        value: i,
        isSelected: i === bucketIndex,
      };
    });
  } else {
    items = [
      {
        label: getRetentionBucketName({bucketIndex, bookmarkParams: query.generateBookmark()}),
        value: bucketIndex,
        isSelected: true,
      },
    ];
  }
  return items;
}
